import { Button } from '@design-system/Button';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system/Dialog';
import { SMPDialogContent } from './SMPDialogContent';

export type SkipBlockReason = 'paused' | 'nod-pending' | 'already-skipped' | 'holding-pattern' | null;

const FALLBACKS: Record<Exclude<SkipBlockReason, null>, { title: string; body: string }> = {
  'paused': {
    title: 'Subscription Paused',
    body: 'Your subscription is currently paused. You cannot skip an order while paused.',
  },
  'nod-pending': {
    title: 'Order Pending',
    body: 'Your next order date is currently pending. Please try again later.',
  },
  'already-skipped': {
    title: "You've already skipped an order",
    body: 'Your next order has already been skipped.',
  },
  'holding-pattern': {
    title: 'Action Needed',
    body: 'Your subscription requires attention before you can skip an order.',
  },
};

export const SkipOrderBlockDialog = ({
  open,
  reason,
  messageHtml,
  ctaLabel,
  onCtaClick,
  onClose,
}: {
  open: boolean;
  reason: SkipBlockReason;
  messageHtml: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  onClose: () => void;
}) => {
  if (!reason) return null;

  const { title, body } = FALLBACKS[reason];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SMPDialogContent>
        <DialogHeader className="gap-6 text-start">
          {messageHtml ? (
            <DialogDescription asChild>
              <div
                className="typography-body text-secondary [&_h2]:typography-h3 [&_h2]:text-primary [&_h2]:mb-4 [&_p]:mb-3"
                dangerouslySetInnerHTML={{ __html: messageHtml }}
              />
            </DialogDescription>
          ) : (
            <>
              <DialogTitle className="typography-h3 text-primary">
                {title}
              </DialogTitle>
              <DialogDescription className="typography-body text-secondary">
                {body}
              </DialogDescription>
            </>
          )}
        </DialogHeader>
        {reason === 'holding-pattern' && ctaLabel && (
          <Button onClick={onCtaClick}>
            {ctaLabel}
          </Button>
        )}
      </SMPDialogContent>
    </Dialog>
  );
};
